"use client";

import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { Globe2, Loader2 } from "lucide-react";
import { locales, type AppLocale } from "@/i18n/config";
import { useAppLocale } from "@/components/AppLocaleProvider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const languageMeta: Record<
  AppLocale,
  { nativeName: string; englishName: string; short: string }
> = {
  en: { nativeName: "English", englishName: "English", short: "EN" },
  de: { nativeName: "Deutsch", englishName: "German", short: "DE" },
  zh: { nativeName: "中文", englishName: "Chinese", short: "ZH" },
  hi: { nativeName: "हिन्दी", englishName: "Hindi", short: "HI" },
  ar: { nativeName: "العربية", englishName: "Arabic", short: "AR" },
};

interface LanguageSwitcherProps {
  compact?: boolean;
}

export default function LanguageSwitcher({
  compact = false,
}: LanguageSwitcherProps) {
  const t = useTranslations("common.language");
  const { locale, setLocale, isPending } = useAppLocale();
  const [selectedLocale, setSelectedLocale] = useState<AppLocale>(locale);

  useEffect(() => {
    setSelectedLocale(locale);
  }, [locale]);

  const currentLabel = useMemo(() => {
    const meta = languageMeta[selectedLocale];
    return compact ? meta.short : meta.nativeName;
  }, [compact, selectedLocale]);

  const handleLocaleChange = (nextLocale: AppLocale) => {
    if (nextLocale === selectedLocale) return;
    setSelectedLocale(nextLocale);
    setLocale(nextLocale);
  };

  return (
    <div className="flex items-center gap-2">
      <span className="sr-only">{t("label")}</span>
      <Select
        value={selectedLocale}
        onValueChange={(value) => handleLocaleChange(value as AppLocale)}
        disabled={isPending}
      >
        <SelectTrigger
          className={cn(
            "cursor-target h-[44px] w-fit rounded-full border-2 border-white/20 bg-[#121a22]/95 py-0 text-sm font-semibold text-white shadow-sm backdrop-blur-sm transition-colors",
            "hover:border-accent/50 hover:bg-[#161f28] ",
            "data-[state=open]:border-accent/50",
            "[&>svg]:ml-1.5 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:shrink-0 [&>svg]:text-white/55 [&>svg]:opacity-100",
            compact
              ? "min-w-[6.75rem] max-w-[8.5rem] pl-3.5 pr-2"
              : "w-[168px] px-3.5",
          )}
          aria-label={t("label")}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2.5 text-white">
            {isPending ? (
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent/15 ring-1 ring-accent/25">
                <Loader2 className="h-3 w-3 animate-spin text-accent" />
              </span>
            ) : (
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent/15 ring-1 ring-accent/25">
                <Globe2 className="h-3 w-3 text-accent" aria-hidden strokeWidth={2} />
              </span>
            )}
            <span className="min-w-0 flex-1 truncate text-left tracking-wide px-2">
              {currentLabel}
            </span>
          </span>
        </SelectTrigger>

        <SelectContent
          position="popper"
          sideOffset={6}
          className="min-w-[220px] rounded-xl border border-white/12 bg-[#141a22] p-1.5 shadow-2xl shadow-black/40"
        >
          {locales.map((lang) => {
            const meta = languageMeta[lang];
            return (
              <SelectItem
                key={lang}
                value={lang}
                textValue={`${meta.englishName} ${meta.nativeName} ${meta.short}`}
                className="cursor-pointer rounded-lg py-2.5 pl-8 pr-3 text-sm focus:outline-none data-[highlighted]:ring-1 data-[highlighted]:ring-white/10"
              >
                <span className="flex w-full min-w-0 items-center gap-3">
                  <span className="flex h-7 w-9 shrink-0 items-center justify-center rounded-md bg-white/[0.06] text-[10px] font-bold uppercase tracking-wider text-accent ring-1 ring-white/10">
                    {meta.short}
                  </span>
                  <span className="min-w-0 flex-1 text-left leading-snug">
                    <span className="block font-medium text-white">
                      {meta.nativeName}
                    </span>
                    <span className="block text-xs font-normal text-white/50">
                      {meta.englishName}
                    </span>
                  </span>
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    </div>
  );
}
